import type { Session } from '@supabase/supabase-js'

export const AUTH_COOKIE_NAME = 'fluity-access-token'
export const DOCS_URL = 'https://docs.fluity.com.br'
export const MAIN_APP_URL = 'https://www.fluity.com.br'

const MAIN_APP_ORIGIN = new URL(MAIN_APP_URL).origin
const SILENT_AUTH_TIMEOUT_MS = 5_000

export function buildMainAppLoginUrl(): string {
  const target = encodeURIComponent(`${window.location.origin}/auth/callback`)
  const crossDomainPath = `/auth/cross-domain?target=${target}`
  return `${MAIN_APP_URL}${crossDomainPath}`
}

/** Attempts to acquire Fluity session tokens silently via a hidden iframe.
 *  Returns `{ access_token, refresh_token }` if the main app already has a
 *  session, or `null` if the user needs to log in (or on timeout/error). */
export function silentCrossDomainAuth(): Promise<{
  access_token: string
  refresh_token: string
} | null> {
  return new Promise((resolve) => {
    const nonce = Math.random().toString(36).slice(2)
    const target = encodeURIComponent(`${window.location.origin}/auth/callback`)
    const src = `${MAIN_APP_URL}/auth/cross-domain?target=${target}&mode=silent&nonce=${encodeURIComponent(nonce)}`

    const iframe = document.createElement('iframe')
    iframe.src = src
    iframe.setAttribute('aria-hidden', 'true')
    iframe.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;border:none;opacity:0;pointer-events:none;'
    document.body.appendChild(iframe)

    let done = false

    const finish = (result: { access_token: string; refresh_token: string } | null) => {
      if (done) return
      done = true
      clearTimeout(timer)
      window.removeEventListener('message', onMessage)
      try { document.body.removeChild(iframe) } catch { /* already removed */ }
      resolve(result)
    }

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== MAIN_APP_ORIGIN) return
      const data = event.data as Record<string, unknown> | null
      if (!data || data.nonce !== nonce) return

      if (data.type === 'fluity_auth_tokens') {
        const { access_token, refresh_token } = data
        if (typeof access_token === 'string' && typeof refresh_token === 'string') {
          finish({ access_token, refresh_token })
          return
        }
      }

      if (data.type === 'fluity_auth_need_login') {
        finish(null)
      }
    }

    window.addEventListener('message', onMessage)
    const timer = setTimeout(() => finish(null), SILENT_AUTH_TIMEOUT_MS)
  })
}

function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined
  const host = window.location.hostname
  if (host === 'localhost' || host === '127.0.0.1') return undefined
  if (host.endsWith('.fluity.com.br') || host === 'fluity.com.br') return '.fluity.com.br'
  return undefined
}

function buildCookieAttributes(maxAge: number): string {
  const domain = getCookieDomain()
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
  const parts = ['path=/', 'SameSite=Lax', `Max-Age=${maxAge}`]
  if (secure) parts.push('Secure')
  if (domain) parts.push(`Domain=${domain}`)
  return parts.join('; ')
}

export function syncAccessTokenCookie(accessToken: string | null): void {
  if (typeof document === 'undefined') return
  const attrs = buildCookieAttributes(accessToken ? 60 * 60 * 24 * 7 : 0)
  if (accessToken) {
    document.cookie = `${AUTH_COOKIE_NAME}=${encodeURIComponent(accessToken)}; ${attrs}`
  } else {
    document.cookie = `${AUTH_COOKIE_NAME}=; ${attrs}`
  }
}

export function syncSessionToCookie(session: Session | null): void {
  syncAccessTokenCookie(session?.access_token ?? null)
}

export function openDocs(session: Session | null): void {
  syncSessionToCookie(session)
  window.location.href = DOCS_URL
}
